<template>
  <HgButton theme="secondary" :disabled="isDisabled" @click="handleClick">
    <template v-if="isLoading" #icon><LoadingSpinner /></template>
    {{
      remainingSeconds > 0
        ? t(resendLabelKey, { resendSeconds: remainingSeconds })
        : t(resendNowLabelKey)
    }}
  </HgButton>

  <HgToast
    v-if="isResendErrorToastVisible"
    data-test="resend-error-toast"
    :isVisible="isResendErrorToastVisible"
    @clear="isResendErrorToastVisible = false"
  >
    <HgNotificationMessage
      data-test="resend-error-notification"
      :title="t('emailVerificationModal.resendError')"
      theme="danger"
    />
  </HgToast>

  <HgToast
    v-if="isResendAlreadyVerifiedToastVisible"
    data-test="resend-already-verified-toast"
    :isVisible="isResendAlreadyVerifiedToastVisible"
    @clear="isResendAlreadyVerifiedToastVisible = false"
  >
    <HgNotificationMessage
      data-test="resend-already-verified-notification"
      :title="t('emailVerificationAlreadyVerified.message')"
      theme="info"
    >
      {{ t('emailVerificationAlreadyVerified.message2') }}
    </HgNotificationMessage>
  </HgToast>

  <HgToast
    v-if="isResendLimitReachedVisible"
    data-test="resend-limit-reached-toast"
    :isVisible="isResendLimitReachedVisible"
    @clear="isResendLimitReachedVisible = false"
  >
    <HgNotificationMessage
      data-test="resend-limit-reached-notification"
      :title="t('emailVerificationModal.resendLimitError')"
      theme="warning"
    />
  </HgToast>

  <HgToast
    v-if="isResendSuccessToastVisible"
    data-test="resend-success-toast"
    :isVisible="isResendSuccessToastVisible"
    @clear="isResendSuccessToastVisible = false"
  >
    <HgNotificationMessage
      data-test="resend-success-notification"
      :title="t('emailVerificationModal.steps.openEmail', { brandName })"
      theme="success"
    />
  </HgToast>
</template>

<script setup lang="ts">
import { isPlatformIs24 } from '@/helpers/platformDetection';
import { useContactFormStore } from '@/store/modules/contact-form';
import { HgButton, HgNotificationMessage, HgToast } from '@smg-real-estate/domus';
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

defineOptions({
  name: 'ResendEmailVerificationButton',
});

const emit = defineEmits<{
  click: [];
}>();

interface Props {
  contactRequestId: string;
  variant?: 'confirmation' | 'code';
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'confirmation',
});

const resendLabelKey = computed((): string =>
  props.variant === 'code' ? 'emailVerificationCodePanel.resend' : 'emailVerificationModal.resend',
);
const resendNowLabelKey = computed((): string =>
  props.variant === 'code'
    ? 'emailVerificationCodePanel.resendNow'
    : 'emailVerificationModal.resendNow',
);

const { t } = useI18n();
const contactFormStore = useContactFormStore();
const LoadingSpinner = defineAsyncComponent(() => import('@/assets/loading_spinner.svg'));

const brandName = isPlatformIs24() ? 'ImmoScout24' : 'Homegate';
const RESEND_COOLDOWN_SECONDS = 60;
const remainingSeconds = ref(RESEND_COOLDOWN_SECONDS);
const isLoading = ref(false);
const isResendErrorToastVisible = ref(false);
const isResendAlreadyVerifiedToastVisible = ref(false);
const isResendLimitReachedVisible = ref(false);
const isResendSuccessToastVisible = ref(false);
let timerId: ReturnType<typeof setInterval> | undefined;

const isDisabled = computed((): boolean => isLoading.value || remainingSeconds.value > 0);

const clearCountdown = () => {
  if (!timerId) return;
  clearInterval(timerId);
  timerId = undefined;
};

const startCountdown = () => {
  clearCountdown();

  timerId = setInterval(() => {
    if (remainingSeconds.value <= 0) {
      clearCountdown();
      return;
    }

    remainingSeconds.value -= 1;
  }, 1000);
};

const handleClick = () => {
  if (isDisabled.value) return;

  void resendConfirmationLink();
};

// eslint-disable-next-line max-statements
const resendConfirmationLink = async () => {
  isLoading.value = true;
  clearCountdown();
  contactFormStore.emailVerificationResendStatus = undefined;

  emit('click');
  await contactFormStore.resendConfirmationLink(props.contactRequestId);

  isLoading.value = false;
  if (contactFormStore.emailVerificationResendStatus === 'OK') {
    isResendSuccessToastVisible.value = true;
    remainingSeconds.value = RESEND_COOLDOWN_SECONDS;
    startCountdown();
    return;
  }

  if (contactFormStore.emailVerificationResendStatus === 'EMAIL_ALREADY_VERIFIED') {
    isResendAlreadyVerifiedToastVisible.value = true;
    remainingSeconds.value = 0;
    return;
  }

  if (contactFormStore.emailVerificationResendStatus === 'RESEND_COUNT_THRESHOLD_REACHED') {
    isResendLimitReachedVisible.value = true;
    remainingSeconds.value = 0;
    return;
  }

  isResendErrorToastVisible.value = true;
  remainingSeconds.value = 0;
};

onMounted(() => {
  startCountdown();
});

onUnmounted(() => {
  clearCountdown();
});
</script>
